import Dexie, { type EntityTable } from "dexie";
import { ALL_VOWS, type Vow, type VowCategory } from "@/data/vows";

// ---------------------------------------------------------------------------
// Dexie Table Interfaces
// ---------------------------------------------------------------------------

export interface VowRecord {
  id: number;
  category: VowCategory;
  title: string;
  titleSanskrit?: string;
  description: string;
  conditions?: string;
  positiveFormulation?: string;
  exceptions?: string;
  karmicCorrelation?: string;
  reflectionQuestions?: string[];
  everydayExamples?: string[];
  alwaysActive: boolean;
}

export interface FavoriteRecord {
  id?: number;
  vowId: number;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Practice Entry (v5) — replaces JournalEntry
// ---------------------------------------------------------------------------

export interface PracticeEntry {
  id: string;                     // UUID (crypto.randomUUID())
  userId: string;
  date: string;                   // YYYY-MM-DD
  timeSlot: 1 | 2 | 3 | 4 | 5 | 6;
  type: "positive" | "negative" | "meditation";  // GETRENNT — nicht verknüpft
  text: string;                   // Was ist passiert? / Notizen (meditation)
  virtueIds?: number[];           // Optional: keine, eine oder mehrere Tugenden

  // Nur bei type='positive' (PFLICHT):
  widmung: string;                // Wem widme ich diese Tat? (IMMER 1:1)

  // Nur bei type='negative':
  versprechen: string;            // Ausgleichshandlung text (UI-Label: "Ausgleichshandlung")
  versprechenBisDatum?: string;   // Deadline Ausgleichshandlung (ISO datetime, optional)
  versprechenErfuellt?: boolean;  // Ausgleichshandlung durchgeführt (default: false)

  promise?: string;               // Versprechen text (optional)
  promiseBisDatum?: string;       // Deadline Versprechen (ISO datetime, optional)
  promiseErfuellt?: boolean;      // Versprechen eingehalten (default: false)

  // Nur bei type='meditation':
  meditationArt?: string;         // Art der Meditation (optional)
  meditationDauer?: number;       // Dauer in Minuten (optional)

  completedAt: string;            // ISO timestamp
}

// ---------------------------------------------------------------------------
// @deprecated — Legacy interfaces for backward compatibility
// ---------------------------------------------------------------------------

/** @deprecated Use PracticeEntry instead */
export interface JournalEntry {
  id?: string;
  userId: string;
  date: string;
  timeSlot: 1 | 2 | 3 | 4 | 5 | 6;
  completedAt: string;
  positive: string;
  negative: string;
  virtueIds: number[] | null;
  widmung: string;
  versprechen: string;
  versprechenBisDatum: string | null;
}

/** @deprecated Legacy check-in interface */
export interface VirtueRating {
  vowId: number;
  rating: 0 | 1 | 2 | 3;
}

/** @deprecated Legacy check-in interface */
export interface CheckInRecord {
  id?: number;
  userId: string;
  date: string;
  timeSlot: 1 | 2 | 3 | 4 | 5 | 6;
  completedAt: string;
  virtues: VirtueRating[];
}

// ---------------------------------------------------------------------------
// Focus Entry
// ---------------------------------------------------------------------------

export interface FocusEntry {
  id: string;               // UUID
  userId: string;
  text: string;             // Vorsatz
  durationDays: number;
  startDate: string;        // YYYY-MM-DD (local)
  endDate: string;          // YYYY-MM-DD (local)
  virtueIds: number[];      // Zehn Tugenden IDs (optional selection)
  createdAt: string;        // ISO timestamp
  completed: boolean;
}

// ---------------------------------------------------------------------------
// Database
// ---------------------------------------------------------------------------

class VajraSilaDB extends Dexie {
  vows!: EntityTable<VowRecord, "id">;
  favorites!: EntityTable<FavoriteRecord, "id">;
  practiceEntries!: EntityTable<PracticeEntry, "id">;
  focusEntries!: EntityTable<FocusEntry, "id">;

  constructor() {
    super("vajrasila");

    this.version(1).stores({
      vows: "id, number, category, title, alwaysActive",
      favorites: "++id, vowId, createdAt",
    });

    this.version(2).stores({
      vows: "id, number, category, title, alwaysActive",
      favorites: "++id, vowId, createdAt",
      checkins: "++id, userId, date, timeSlot, completedAt, [userId+date]",
    });

    this.version(3).stores({
      vows: "id, number, category, title, alwaysActive",
      favorites: "++id, vowId, createdAt",
      checkins: null,
      journalEntries: "++id, [userId+date], userId, date",
    });

    this.version(4).stores({
      vows: "id, number, category, title, alwaysActive",
      favorites: "++id, vowId, createdAt",
      journalEntries: null, // Delete old journalEntries table
      practiceEntries: "id, [userId+date], userId, date, type",
    });

    // v5: virtueId (single) → virtueIds (array)
    this.version(5)
      .stores({
        vows: "id, number, category, title, alwaysActive",
        favorites: "++id, vowId, createdAt",
        practiceEntries: "id, [userId+date], userId, date, type",
      })
      .upgrade(async (trans) => {
        await trans
          .table("practiceEntries")
          .toCollection()
          .modify((entry: Record<string, unknown>) => {
            if (entry.virtueId && !entry.virtueIds) {
              entry.virtueIds = [entry.virtueId as string];
            }
            delete entry.virtueId;
          });
      });

    // v6: focusEntries table added
    this.version(6).stores({
      vows: "id, number, category, title, alwaysActive",
      favorites: "++id, vowId, createdAt",
      practiceEntries: "id, [userId+date], userId, date, type",
      focusEntries: "id, userId, completed",
    });
  }
}

export const db = new VajraSilaDB();

// ---------------------------------------------------------------------------
// Seed: Populate vows table if empty
// ---------------------------------------------------------------------------

export async function seedVowsIfNeeded(): Promise<void> {
  const count = await db.vows.count();
  if (count === 0) {
    const records: VowRecord[] = ALL_VOWS.map((v: Vow) => ({
      id: v.id,
      category: v.category,
      title: v.de.title,
      titleSanskrit: v.titleSanskrit,
      description: v.de.description,
      conditions: v.de.conditions,
      positiveFormulation: v.de.positiveFormulation,
      exceptions: v.de.exceptions,
      karmicCorrelation: v.de.karmicCorrelation,
      reflectionQuestions: v.de.reflectionQuestions,
      everydayExamples: v.de.everydayExamples,
      alwaysActive: v.alwaysActive,
    }));
    await db.vows.bulkAdd(records);
  }
}
